import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import AsyncSelect from 'react-select/async';

import CustomDropdown from '@components/CustomDropdown';
import ModalContainer from '@components/modals/ModalContainer';
import { toastError } from '@components/Toast';

import { closeLoader, openLoader } from '@store/actions/LoaderAction';

import { fetchDepartments, fetchTeamBrands, fetchUserByEmail } from '@services/commonService';

import ApiClient from '@lib/apiClient';

import { changeDateFormat, convertNumberToUSFormat, getNumberUnit, isPageReachBottom } from '@utils/Util';

import { BASE_URL } from '@/constants/constant';

const BDMList = ({ onTabChange, teamInfo }) => {
	const [dataList, setDataList] = useState([]);
	const [departments, setDepartments] = useState([]);
	const [bdms, setBdms] = useState([]);
	const [, setBrandList] = useState([]);
	const [searchName, setSearchName] = useState('');
	const [clientList, setClientList] = useState([]);
	const [, setUserList] = useState([]);
	const [inputData, setInputData] = useState({});
	const [pageNumber, setPageNumber] = useState(0);
	const [isCreate, setIsCreate] = useState(false);
	const dispatch = useDispatch();
	const { id } = useParams();
	const myRef = useRef();
	const isGlobalLoading = useSelector((store) => store.loaderStore);
	const navigate = useNavigate();

	const fetchBDMList = async (search) => {
		try {
			let params = `?brandId=${id}`;
			if (search) {
				params += `&name=${search}`;
			}
			const { data } = await ApiClient.GET('BDMUsers', params);
			let modifiedData = data.map((item) => ({ id: item.id, value: item.name }));
			setBdms(modifiedData);
		} catch (error) {
			toastError(error.message);
		}
	};

	useEffect(() => {
		onFetchBrandList();
		fetchBDMList();
		fetchDepartments().then((res) => {
			let tmpDepartments = res.map((item) => ({ id: item.id, value: item.name }));
			setDepartments(tmpDepartments);
		});
		document.addEventListener('scroll', handleScroll);
		return () => {
			document.removeEventListener('scroll', handleScroll);
		};
	}, []);

	const onFetchBrandList = async () => {
		setBrandList(await fetchTeamBrands(`${id}?withMemberCount=true`));
	};

	const fetchBuyersOrBDMS = async () => {
		try {
			dispatch(openLoader());
			let params = `?page=${pageNumber}&brandTeamId=${teamInfo?.id}&size=20&sort=id,desc`;
			if (inputData?.department?.id) {
				params += `&productGroupId=${inputData.department.id}`;
			}
			if (inputData?.bdm?.id) {
				params += `&projectedById=${inputData.bdm.id}`;
			}
			const { data } = await ApiClient.GET(
				`${BASE_URL}/projection/brands/${id}/buyer-activity-details`,
				params,
				true
			);
			if (data.currentPage > 0) {
				setDataList((prev) => [...prev, ...data.data]);
			} else {
				setDataList(data.data);
			}
			myRef.current = data;
			dispatch(closeLoader());
		} catch (error) {
			toastError(error.message);
			dispatch(closeLoader());
		}
	};

	useEffect(() => {
		fetchBuyersOrBDMS();
	}, [pageNumber, inputData]);

	const handleScroll = () => {
		if (isPageReachBottom() && myRef && myRef.current) {
			let { totalElements, currentPage, data } = myRef.current;
			if (totalElements && totalElements > 0 && totalElements !== data.length) {
				setPageNumber(currentPage + 1);
			}
		}
	};

	const styles = {
		control: (base) => ({
			...base,
			fontFamily: 'Inter',
			fontSize: '14px'
		}),
		menu: (base) => ({
			...base,
			fontFamily: 'Inter',
			fontSize: '14px'
		})
	};

	const onChangeMultiSelect = (selected) => {
		setClientList(selected?.map((item) => item?.value));
	};

	const onDelete = async (id) => {
		try {
			dispatch(openLoader());
			await ApiClient.DELETE('getTeamBrands', {}, `${teamInfo?.id}/client/${id}`);
			fetchBuyersOrBDMS();
			dispatch(closeLoader());
		} catch (error) {
			dispatch(closeLoader());
			if (error.message) {
				toastError(error.message);
			} else {
				toastError('Something went wrong! Please try again.');
			}
		}
	};

	const renderTableBody = () => {
		return dataList?.map((item) => {
			return (
				<tr key={`bdm_user_${item?.buyerResponse?.id}`}>
					<td>
						<div className="d-flex align-items-center">
							<div className="buyer-image">
								{item?.buyerResponse?.profilePicDocument?.docUrl && (
									<img
										src={item?.buyerResponse?.profilePicDocument?.docUrl}
										alt="profile"
										className="profile"
									/>
								)}
							</div>
							<div className="buyer-info ml-2">
								<p
									className="regular-14 gray_dark_01 mb-1 cursor-pointer"
									onClick={() => navigate(`/clients/edit/${item?.buyerResponse?.id}`)}
								>
									{item?.buyerResponse?.name}
								</p>
								<p className="regular-12 gray_dark_02 mb-1">
									{item?.buyerResponse?.designation}
								</p>
								<p className="regular-12 gray_dark_02">{item?.buyerResponse?.email}</p>
							</div>
						</div>
					</td>
					<td>{item?.productGroupName}</td>
					<td>{item?.seasonName}</td>
					<td>
						<p className="mb-1">{item?.bdmName}</p>
						<p className="mb-1">
							T: {item?.targetAmount > 0 ? ` $${getNumberUnit(item?.targetAmount)} ` : ''}
						</p>
						<p className>
							A:{' '}
							{item?.achievementAmount > 0
								? ` $${getNumberUnit(item?.achievementAmount)} `
								: ''}
						</p>
					</td>
					<td>
						<p className="mb-1">
							Sample:{' '}
							<span className="gray_dark_01">
								{item?.sample > 0 ? ` $${convertNumberToUSFormat(item?.sample)} ` : ''}
							</span>
						</p>
						<p className="mb-1">
							Quotes:{' '}
							<span className="gray_dark_01">
								{item?.quoteAmount > 0
									? ` $${convertNumberToUSFormat(item?.quoteAmount)} `
									: ''}
							</span>
						</p>
						<p>
							Order:{' '}
							<span className="gray_dark_01">
								{item?.orderAmount > 0
									? ` $${convertNumberToUSFormat(item?.orderAmount)} `
									: ''}
							</span>
						</p>
					</td>
					<td>{changeDateFormat(item?.dateAdded, '', 'DD MMM, YYYY')}</td>
					<td>
						<button
							className="button text ml-12"
							disabled={isGlobalLoading}
							onClick={() => onDelete(item?.buyerResponse?.id)}
						>
							<img
								src={`${process.env.PUBLIC_URL}/icons/delete_red.svg`}
								alt="delete"
								className="delete no-padding"
							/>
						</button>
					</td>
				</tr>
			);
		});
	};

	const onCustomDropdownClick = (obj, fieldName) => {
		setInputData((prev) => ({ ...prev, [fieldName]: { id: obj.id, value: obj.value } }));
	};

	const searchUserByEmail = async () => {
		let params = `?page=0&size=100&notTeamAssigned=true&brandId=${id}&email=${searchName}`;
		let response = await fetchUserByEmail(params);
		return response?.map((item) => ({
			value: item.id,
			label: item.name
		}));
	};

	const promiseOptions = () =>
		new Promise((resolve) => {
			setTimeout(() => {
				setUserList(resolve(searchUserByEmail()));
			}, 1000);
		});

	const onAddClients = async () => {
		try {
			dispatch(openLoader());
			await ApiClient.POST('getTeamBrands', {}, `${teamInfo?.id}/add-client?clientIds=${clientList}`);
			fetchBuyersOrBDMS();
			setIsCreate(false);
			dispatch(closeLoader());
		} catch (error) {
			dispatch(closeLoader());
			if (error.message) {
				toastError(error.message);
			} else {
				toastError('Something went wrong! Please try again.');
			}
		}
	};

	return (
		<div className="tab-contents">
			<div className="d-flex justify-content-between align-items-center mb-12">
				<h3
					className="semibold-16 mb-0 d-flex align-content-center cursor-pointer"
					onClick={() => onTabChange('BRAND_TEAM')}
				>
					<img
						src={`${process.env.PUBLIC_URL}/icons/back-arrow-gray.svg`}
						alt="back"
						className="mr-1"
					/>{' '}
					{teamInfo?.name}({teamInfo?.memberCount || 0})
				</h3>
				<div className="d-flex justify-content-end transparent-select">
					<div className="single-select mr-3">
						<CustomDropdown
							type="addItem"
							title={'BDM'}
							isShowLabel={false}
							onItemClick={(value) => onCustomDropdownClick(value, 'bdm')}
							onItemSearch={(value) => fetchBDMList(value)}
							items={bdms}
							selectedItem={inputData?.bdm?.value || 'BDM'}
						/>
					</div>
					<div className="single-select mr-12">
						<CustomDropdown
							type="addItem"
							title={'Department name'}
							isShowLabel={false}
							onItemClick={(value) => onCustomDropdownClick(value, 'department')}
							items={departments}
							selectedItem={inputData?.department?.value || 'Department'}
						/>
					</div>
					<button className="button text ml-12" onClick={() => setIsCreate(true)}>
						<img
							src={`${process.env.PUBLIC_URL}/icons/add-round.svg`}
							alt="add"
							className="mr-1"
						/>
						<span className="mr-1 gray_dark_01">Add Client</span>
					</button>
				</div>
			</div>
			<table className="table with-border">
				<thead>
					<tr>
						<th>Buyer Name</th>
						<th>Department</th>
						<th>Season</th>
						<th>BDM</th>
						<th>Running Activities</th>
						<th>Member added</th>
						<th>Action</th>
					</tr>
				</thead>
				<tbody>
					{/* {dataList?.length === 0 ? (
						<div className="d-flex justify-content-center align-items-center">
							<p>no member found</p>
						</div>
					) : (
						renderTableBody()
					)} */}
					{renderTableBody()}
				</tbody>
			</table>

			{isCreate && (
				<ModalContainer onCloseModal={() => setIsCreate(false)}>
					<div className="common-popup shadow-24dp">
						<div className="common-popup-header d-flex justify-content-between">
							<div className="popup-tilte">
								<h3 className="semibold-16 mb-0"> Add Client</h3>
							</div>
							<div className="close-btn" onClick={() => setIsCreate(false)}>
								<img src={`${process.env.PUBLIC_URL}/icons/close.svg`} alt="close" />
							</div>
						</div>
						<div className="common-popup-body">
							<div className="container px-0">
								<div className="row">
									<div className="col-md-12">
										<div className="form-group no-shadow-react-select">
											<label htmlFor="operationalZone">Select Client</label>
											<AsyncSelect
												styles={styles}
												onInputChange={(v) => setSearchName(v)}
												onChange={(selected) =>
													onChangeMultiSelect(selected, 'userIds')
												}
												isMulti
												cacheOptions
												defaultOptions
												loadOptions={promiseOptions}
											/>
										</div>
									</div>
								</div>
							</div>
						</div>
						<div className="modal-footer">
							<div className="footer-buttons text-right">
								<button
									className="button gray-outline size32"
									onClick={() => setIsCreate(false)}
								>
									Cancel
								</button>
								<button
									className="button size32 ml-2"
									disabled={isGlobalLoading}
									onClick={() => onAddClients()}
								>
									Add
								</button>
							</div>
						</div>
					</div>
				</ModalContainer>
			)}
		</div>
	);
};

export default BDMList;
